import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import type { ReportDataset } from '@/features/reporting/types';
import {
  emptySchedule,
  FREQUENCY_LABELS,
  METRIC_OPTIONS,
  PRIORITY_LABELS,
  TIMEZONES,
  WEEKDAYS,
  type Frequency,
  type Priority,
  type ProfileSchedule,
} from '@/features/scheduler/types';

const input =
  'h-9 w-full rounded-lg border border-ink-200 bg-white px-3 text-sm text-ink-900 shadow-soft transition focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:border-white/10 dark:bg-ink-900 dark:text-ink-50';
const label = 'mb-1 block text-xs font-medium text-ink-600 dark:text-ink-300';

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }): JSX.Element {
  return (
    <section className="border-t border-ink-100 pt-5 first:border-t-0 first:pt-0 dark:border-white/5">
      <h4 className="font-display text-sm font-semibold text-ink-900 dark:text-ink-50">{title}</h4>
      {hint && <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Toggle({
  checked,
  onChange,
  text,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  text: string;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-brand-600" />
      {text}
    </label>
  );
}

export function ScheduleFormModal({
  open,
  initial,
  datasets,
  onSave,
  onClose,
}: {
  open: boolean;
  initial: ProfileSchedule | null;
  datasets: ReportDataset[];
  onSave: (s: ProfileSchedule) => void;
  onClose: () => void;
}): JSX.Element | null {
  const [s, setS] = useState<ProfileSchedule>(() => initial ?? emptySchedule());
  if (!open) return null;

  const set = <K extends keyof ProfileSchedule>(k: K, v: ProfileSchedule[K]) => setS((p) => ({ ...p, [k]: v }));

  function toggleDataset(d: ReportDataset): void {
    setS((p) => {
      const has = p.datasetIds.includes(d.id);
      return {
        ...p,
        datasetIds: has ? p.datasetIds.filter((x) => x !== d.id) : [...p.datasetIds, d.id],
        datasetNames: has ? p.datasetNames.filter((x) => x !== d.name) : [...p.datasetNames, d.name],
      };
    });
  }

  function toggleMetric(key: string): void {
    setS((p) => ({
      ...p,
      metrics: p.metrics.includes(key) ? p.metrics.filter((m) => m !== key) : [...p.metrics, key],
    }));
  }

  const valid = s.name.trim() !== '' && (s.datasetIds.length > 0 || s.datasetNames.length > 0);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/40 px-4 py-10 backdrop-blur-sm dark:bg-black/60"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-elevated dark:border-white/10 dark:bg-ink-900"
      >
        <header className="flex items-start justify-between gap-4 border-b border-ink-100 px-6 py-4 dark:border-white/5">
          <div>
            <h3 className="font-display text-base font-semibold tracking-tight text-ink-900 dark:text-ink-50">
              {initial ? 'Edit schedule' : 'New profiling schedule'}
            </h3>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              Configure what to profile, when to run it, and how failures are handled.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-ink-400 hover:bg-ink-100 dark:hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
          {/* Overview */}
          <Section title="Overview">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <span className={label}>Name</span>
                <input className={input} value={s.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Customer Master — Daily Profiling" />
              </div>
              <div className="sm:col-span-2">
                <span className={label}>Description</span>
                <input className={input} value={s.description} onChange={(e) => set('description', e.target.value)} placeholder="What this job checks and why" />
              </div>
              <div>
                <span className={label}>Owner</span>
                <input className={input} value={s.owner} onChange={(e) => set('owner', e.target.value)} />
              </div>
              <div className="flex items-end pb-1">
                <Toggle checked={s.enabled} onChange={(v) => set('enabled', v)} text="Enabled" />
              </div>
            </div>
          </Section>

          {/* Target */}
          <Section title="Target" hint="Pick the datasets to profile.">
            <div className="max-h-36 overflow-y-auto rounded-lg border border-ink-200 p-2 dark:border-white/10">
              {datasets.length === 0 ? (
                <p className="px-1 text-xs text-ink-400 dark:text-ink-500">No datasets available — connect a source first.</p>
              ) : (
                datasets.map((d) => (
                  <label key={d.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-ink-700 hover:bg-ink-50 dark:text-ink-200 dark:hover:bg-white/5">
                    <input type="checkbox" checked={s.datasetIds.includes(d.id)} onChange={() => toggleDataset(d)} className="h-4 w-4 accent-brand-600" />
                    <span>{d.name}</span>
                    {d.connector && <span className="text-xs text-ink-400 dark:text-ink-500">· {d.connector}</span>}
                  </label>
                ))
              )}
            </div>
            <div className="mt-3">
              <span className={label}>Column scope</span>
              <select className={input} value={s.scope} onChange={(e) => set('scope', e.target.value as ProfileSchedule['scope'])}>
                <option value="all_columns">All columns</option>
                <option value="selected">Selected columns only</option>
              </select>
            </div>
          </Section>

          {/* Schedule */}
          <Section title="Schedule">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <span className={label}>Frequency</span>
                <select className={input} value={s.frequency} onChange={(e) => set('frequency', e.target.value as Frequency)}>
                  {(Object.keys(FREQUENCY_LABELS) as Frequency[]).map((f) => (
                    <option key={f} value={f}>{FREQUENCY_LABELS[f]}</option>
                  ))}
                </select>
              </div>
              {s.frequency !== 'hourly' && s.frequency !== 'cron' && (
                <div>
                  <span className={label}>Time</span>
                  <input type="time" className={input} value={s.time} onChange={(e) => set('time', e.target.value)} />
                </div>
              )}
              {s.frequency === 'weekly' && (
                <div>
                  <span className={label}>Day of week</span>
                  <select className={input} value={s.dayOfWeek} onChange={(e) => set('dayOfWeek', Number(e.target.value))}>
                    {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
              )}
              {s.frequency === 'monthly' && (
                <div>
                  <span className={label}>Day of month</span>
                  <input type="number" min={1} max={28} className={input} value={s.dayOfMonth} onChange={(e) => set('dayOfMonth', Number(e.target.value))} />
                </div>
              )}
              {s.frequency === 'cron' && (
                <div className="sm:col-span-2">
                  <span className={label}>Cron expression</span>
                  <input className={`${input} font-mono`} value={s.cron} onChange={(e) => set('cron', e.target.value)} placeholder="0 2 * * *" />
                </div>
              )}
              <div>
                <span className={label}>Timezone</span>
                <select className={input} value={s.timezone} onChange={(e) => set('timezone', e.target.value)}>
                  {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <span className={label}>Start date (optional)</span>
                <input type="date" className={input} value={s.startDate} onChange={(e) => set('startDate', e.target.value)} />
              </div>
              <div>
                <span className={label}>End date (optional)</span>
                <input type="date" className={input} value={s.endDate} onChange={(e) => set('endDate', e.target.value)} />
              </div>
            </div>
          </Section>

          {/* Profiling options */}
          <Section title="Profiling options">
            <span className={label}>Metrics to compute</span>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {METRIC_OPTIONS.map((m) => (
                <Toggle key={m.key} checked={s.metrics.includes(m.key)} onChange={() => toggleMetric(m.key)} text={m.label} />
              ))}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <span className={label}>Scan mode</span>
                <select className={input} value={s.scanMode} onChange={(e) => set('scanMode', e.target.value as ProfileSchedule['scanMode'])}>
                  <option value="full">Full scan</option>
                  <option value="sample">Sample</option>
                </select>
              </div>
              {s.scanMode === 'sample' && (
                <div>
                  <span className={label}>Sample size (%)</span>
                  <input type="number" min={1} max={100} className={input} value={s.samplePercent} onChange={(e) => set('samplePercent', Number(e.target.value))} />
                </div>
              )}
              <div className="flex items-end pb-1">
                <Toggle checked={s.incremental} onChange={(v) => set('incremental', v)} text="Incremental (changed rows only)" />
              </div>
            </div>
          </Section>

          {/* Execution */}
          <Section title="Execution & retries">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <span className={label}>Priority</span>
                <select className={input} value={s.priority} onChange={(e) => set('priority', e.target.value as Priority)}>
                  {(Object.keys(PRIORITY_LABELS) as Priority[]).map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
                </select>
              </div>
              <div>
                <span className={label}>Max concurrency</span>
                <input type="number" min={1} max={16} className={input} value={s.maxConcurrency} onChange={(e) => set('maxConcurrency', Number(e.target.value))} />
              </div>
              <div>
                <span className={label}>Timeout (min)</span>
                <input type="number" min={1} className={input} value={s.timeoutMinutes} onChange={(e) => set('timeoutMinutes', Number(e.target.value))} />
              </div>
              <div>
                <span className={label}>Retries</span>
                <input type="number" min={0} max={10} className={input} value={s.retries} onChange={(e) => set('retries', Number(e.target.value))} />
              </div>
              <div>
                <span className={label}>Retry backoff</span>
                <select className={input} value={s.backoff} onChange={(e) => set('backoff', e.target.value as ProfileSchedule['backoff'])}>
                  <option value="fixed">Fixed</option>
                  <option value="exponential">Exponential</option>
                </select>
              </div>
            </div>
          </Section>

          {/* Notifications */}
          <Section title="Notifications & alerts">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <span className={label}>Email recipients</span>
                <input className={input} value={s.notifyEmails} onChange={(e) => set('notifyEmails', e.target.value)} placeholder="team@bank.example, lead@bank.example" />
              </div>
              <div>
                <span className={label}>Webhook URL (optional)</span>
                <input className={input} value={s.webhookUrl} onChange={(e) => set('webhookUrl', e.target.value)} placeholder="https://hooks…" />
              </div>
              <div className="sm:col-span-2 flex flex-wrap gap-4">
                <Toggle checked={s.notifyOnSuccess} onChange={(v) => set('notifyOnSuccess', v)} text="On success" />
                <Toggle checked={s.notifyOnFailure} onChange={(v) => set('notifyOnFailure', v)} text="On failure" />
                <Toggle checked={s.notifyOnAnomaly} onChange={(v) => set('notifyOnAnomaly', v)} text="On anomaly detected" />
              </div>
              <div>
                <span className={label}>Alert if quality below (%) — 0 to disable</span>
                <input type="number" min={0} max={100} className={input} value={s.alertThreshold} onChange={(e) => set('alertThreshold', Number(e.target.value))} />
              </div>
            </div>
          </Section>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-ink-100 px-6 py-4 dark:border-white/5">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-white/5">
            Cancel
          </button>
          <button
            type="button"
            disabled={!valid}
            onClick={() => onSave(s)}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {initial ? 'Save changes' : 'Create schedule'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
